import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Upload,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Trash2,
  Send,
  Banknote,
  Building2,
  Plus,
  ChevronDown,
  ChevronUp,
  Paperclip,
} from "lucide-react";
import api from "../services/api";
import toast from "react-hot-toast";
import HelpButton from "../components/HelpButton";
import { isReadOnlyStage } from "../constants/applicationStages";

/* ================================================================
 * Helpers
 * ================================================================ */
const formatCurrency = (val) => {
  if (val === null || val === undefined || val === "" || val === "-") return "-";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const cellText = (cell) => {
  if (cell === null || cell === undefined) return "-";
  if (typeof cell === "object") {
    const v = cell.value;
    if (v === null || v === undefined || v === "") return "-";
    return String(v) + (cell.tail ? ` ${cell.tail}` : "");
  }
  return String(cell);
};

const STATUS_STYLES = {
  success: "bg-green-100 text-green-700 border-green-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  failed: "bg-red-100 text-red-700 border-red-200",
  neutral: "bg-gray-100 text-gray-700 border-gray-200",
};

const checkBucket = (status) => {
  if (!status) return "neutral";
  const s = String(status).toLowerCase();
  if (
    s.includes("success") ||
    s.includes("complete") ||
    s.includes("done") ||
    s.includes("active") ||
    s.includes("approved") ||
    s.includes("verified") ||
    s.includes("match")
  )
    return "success";
  if (s.includes("fail") || s.includes("reject") || s.includes("error"))
    return "failed";
  if (s.includes("pending") || s.includes("wip") || s.includes("initiat"))
    return "pending";
  return "neutral";
};

/* ================================================================
 * Generic dynamic table renderer (mobile-friendly)
 * ================================================================ */
function DynamicTable({ title, columns, rows, additionalHeaders, defaultOpen = false, compact = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!rows || rows.length === 0) return null;

  // Default column when none provided
  const cols =
    columns && columns.length > 0
      ? columns
      : [{ label: "Amount", name: "amount" }];

  // Determine the "side" column (heading column) vs value columns
  const valueCols = cols.filter(
    (c) => c.name !== "sidecolumns" && c.name !== "breakup"
  );

  const cellPad = compact ? "px-1 py-1.5" : "px-3 py-2";
  const cellTxt = compact ? "text-[10px]" : "text-[12px]";
  const headTxt = compact ? "text-[10px]" : "text-[11px]";

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-3">
      {title && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition-colors"
        >
          <span>{title}</span>
          {open ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>
      )}
      {open && (
      <div className={compact ? "" : "overflow-x-auto"}>
        <table className={`w-full text-xs ${compact ? "table-fixed" : ""}`}>
          {(additionalHeaders?.length || valueCols.length > 1) && (
            <thead>
              {additionalHeaders?.length > 0 && (
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className={`${cellPad} text-left ${headTxt} font-semibold text-gray-600 dark:text-gray-300`} />
                  {additionalHeaders.map((h, i) => (
                    <th
                      key={i}
                      colSpan={h.colSpan}
                      className={`${cellPad} text-center ${headTxt} font-bold text-indigo-700 dark:text-indigo-300 border-l border-gray-200 dark:border-gray-700`}
                    >
                      {h.heading}
                    </th>
                  ))}
                </tr>
              )}
              <tr className="bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                <th className={`${cellPad} text-left ${headTxt} font-semibold text-gray-600 dark:text-gray-300`}>
                  {cols[0]?.label || ""}
                </th>
                {valueCols.map((c) => (
                  <th
                    key={c.name}
                    className={`${cellPad} text-right ${headTxt} font-semibold text-gray-600 dark:text-gray-300 ${compact ? "break-words" : "whitespace-nowrap"}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, idx) => {
              const isHeader = row.isHeading;
              return (
                <tr
                  key={idx}
                  className={`border-t border-gray-100 dark:border-gray-700 ${
                    isHeader ? "bg-indigo-50 dark:bg-indigo-900/30 font-bold" : ""
                  }`}
                >
                  <td className={`${cellPad} text-gray-800 dark:text-gray-200 ${cellTxt} ${compact ? "break-words" : ""}`}>
                    {row.heading}
                  </td>
                  {valueCols.map((c) => (
                    <td
                      key={c.name}
                      className={`${cellPad} text-right text-gray-700 dark:text-gray-300 ${cellTxt} ${compact ? "break-words" : "whitespace-nowrap"}`}
                    >
                      {cellText(row.values?.[c.name])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

/* ================================================================
 * Section: Offer Payment Summary
 * ================================================================ */
function OfferPaymentSummarySection({ data, product }) {
  if (!data) return null;
  const ops = data.offer_payment_summary;
  const dp = data.down_payment;
  const interests = data.interests;
  const total = data.total;
  const rts = data.rts_asset;
  const cd = data.charges_due;

  return (
    <Section title="Offer Payment Summary" icon={<FileText className="w-4 h-4" />}>
      {ops && (
        <DynamicTable
          title="Summary"
          columns={ops.columns}
          rows={ops.rows}
        />
      )}
      {dp && (
        <DynamicTable
          title="Down Payment"
          columns={dp.columns}
          rows={dp.rows}
          additionalHeaders={dp.additional_headers}
        />
      )}
      {interests && (
        <DynamicTable title="Interests" columns={interests.columns} rows={interests.rows} />
      )}
      {total && (
        <DynamicTable title="Totals" columns={total.columns} rows={total.rows} />
      )}
      {product === "RTS" && rts && (
        <DynamicTable title="Asset" columns={rts.columns} rows={rts.rows} />
      )}
      {cd && <ChargesDueTables data={cd} />}
    </Section>
  );
}

/* ================================================================
 * Charges Due — split into two tables
 *   1) Total Charges due With Customer: Breakup | Amount | GST |
 *      Total Amount | Recovery Method
 *   2) Total Recovery: row per recovery method with totals
 * ================================================================ */
function ChargesDueTables({ data }) {
  const rows = data?.rows || [];
  if (rows.length === 0) return null;

  const RECOVERY_COLS = [
    { name: "added_loan_amt", label: "Funded In Loan Amt." },
    { name: "deduct_from_disbursement", label: "Deduct from Disbursement" },
    { name: "upfront_collection", label: "Upfront Collection" },
  ];

  // Override column labels from API if present
  const colByName = {};
  (data.columns || []).forEach((c) => {
    colByName[c.name] = c.label;
  });
  const recoveryCols = RECOVERY_COLS.map((rc) => ({
    ...rc,
    label: colByName[rc.name] || rc.label,
  }));

  const hasValue = (cell) => {
    const v = typeof cell === "object" ? cell?.value : cell;
    return v !== null && v !== undefined && v !== "" && v !== "-" && v !== 0;
  };

  // Locate totals row (heading contains "total charges")
  const totalsIdx = rows.findIndex((r) =>
    String(r.heading || "").toLowerCase().includes("total charges")
  );
  const totalsRow = totalsIdx >= 0 ? rows[totalsIdx] : rows[rows.length - 1];
  const detailRows = rows.filter((_, i) =>
    totalsIdx >= 0 ? i !== totalsIdx : i !== rows.length - 1
  );

  // Build charges table rows (5 columns)
  const chargesColumns = [
    { label: "Amount", name: "amount" },
    { label: "GST", name: "gst" },
    { label: "Total Amount", name: "total_amount" },
    { label: "Recovery Method", name: "recovery_method" },
  ];

  const chargesRows = detailRows.map((r) => {
    const methods = recoveryCols
      .filter((rc) => hasValue(r.values?.[rc.name]))
      .map((rc) => rc.label);
    return {
      heading: r.heading,
      isHeading: r.isHeading,
      values: {
        amount: r.values?.amount,
        gst: r.values?.gst,
        total_amount: r.values?.total_amount,
        recovery_method: methods.length > 0 ? methods.join(", ") : "-",
      },
    };
  });

  // Append totals row at bottom of charges table
  if (totalsRow) {
    chargesRows.push({
      heading: totalsRow.heading || "Total",
      isHeading: true,
      values: {
        amount: totalsRow.values?.amount,
        gst: totalsRow.values?.gst,
        total_amount: totalsRow.values?.total_amount,
        recovery_method: "-",
      },
    });
  }

  // Build "Total Recovery" rows from totals row
  const recoveryRows = recoveryCols.map((rc) => ({
    heading: rc.label,
    isHeading: false,
    values: {
      amount: totalsRow?.values?.[rc.name] ?? "-",
    },
  }));

  return (
    <>
      <DynamicTable
        title="Total Charges due With Customer"
        columns={[{ label: "Break up", name: "breakup" }, ...chargesColumns]}
        rows={chargesRows}
        compact
      />
      <DynamicTable
        title="Total Recovery"
        columns={[
          { label: "Recovery Method", name: "sidecolumns" },
          { label: "Amount", name: "amount" },
        ]}
        rows={recoveryRows}
      />
    </>
  );
}

/* ================================================================
 * Section: Disbursement Checks
 * ================================================================ */
function DisbursementChecksSection({ checks }) {
  if (!checks) return null;
  const groups = Object.entries(checks);

  // Flatten into rows: one row per item, with the check (group) name in col 1.
  const rows = [];
  groups.forEach(([groupName, items]) => {
    if (!items || items.length === 0) {
      rows.push({ check: groupName, typeResult: "-", status: null, date_time: null });
      return;
    }
    items.forEach((item) => {
      const typeParts = [];
      const t = item.profile_type || item.type;
      if (t && t !== groupName) typeParts.push(t);
      if (item.result) typeParts.push(item.result);
      rows.push({
        check: groupName,
        typeResult: typeParts.length > 0 ? typeParts.join(" • ") : "-",
        status: item.status || null,
        date_time: item.date_time || null,
      });
    });
  });

  const StatusIcon = ({ bucket }) => {
    if (bucket === "success")
      return <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />;
    if (bucket === "failed")
      return <XCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />;
    if (bucket === "pending")
      return <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />;
    return null;
  };

  return (
    <Section title="Disbursement Checks" icon={<CheckCircle2 className="w-4 h-4" />}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full table-fixed text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 w-[32%]">
                Check
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 w-[36%]">
                Type / Result
              </th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 w-[32%]">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-3 text-xs text-gray-500 text-center">
                  No data
                </td>
              </tr>
            )}
            {rows.map((r, idx) => {
              const bucket = checkBucket(r.status || r.typeResult);
              return (
                <tr
                  key={idx}
                  className="border-t border-gray-100 dark:border-gray-700 align-top"
                >
                  <td className="px-2 py-2 text-[11px] font-semibold text-gray-800 dark:text-gray-200 break-words">
                    {r.check}
                  </td>
                  <td className="px-2 py-2 text-[11px] text-gray-700 dark:text-gray-300 break-words">
                    {r.typeResult}
                  </td>
                  <td className="px-2 py-2 break-words">
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${STATUS_STYLES[bucket]}`}
                    >
                      <StatusIcon bucket={bucket} />
                      <span className="break-words">{r.status || "—"}</span>
                    </span>
                    {r.date_time && (
                      <p className="text-[9px] text-gray-400 mt-1">{r.date_time}</p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/* ================================================================
 * Section wrapper
 * ================================================================ */
function Section({ title, icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-4 bg-white/60 dark:bg-gray-800/60 backdrop-blur rounded-3xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30"
      >
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 text-white flex items-center justify-center">
            {icon}
          </span>
          <span className="text-sm font-bold text-gray-800 dark:text-gray-100">
            {title}
          </span>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {open && <div className="p-3">{children}</div>}
    </div>
  );
}

/* ================================================================
 * Document Upload Card (one per doc type)
 * ================================================================ */
const DOC_FIELDS = {
  INVOICE: { label: "Invoice", numberLabel: "Invoice No.", amountLabel: "Amount", multi: false, fullAmountToggle: true, dateLabel: "Invoice Date" },
  MMR: { label: "MMR", numberLabel: "MMR No.", amountLabel: "MMR Amount", multi: false, fullAmountToggle: true, dateLabel: "MMR Date" },
  DISCOM: { label: "DISCOM", numberLabel: "DISCOM Application No.", amountLabel: "Sanctioned Load (kw)", multi: false, dateLabel: "Sanction Date" },
  DPR: { label: "DPR", numberLabel: "DPR No.", amountLabel: "DPR Capacity (kw)", multi: true, hasAssetCost: true, dateLabel: "DPR Date" },
  RTO_FORM: { label: "RTO Form", numberLabel: "RTO No.", multi: false, dateLabel: "RTO Date" },
  VEHICLE_INSURANCE: { label: "Vehicle Insurance", numberLabel: "Insurance No.", amountLabel: "Insurance Amount", multi: false, dateLabel: "Insurance Date" },
};

function DocumentUploadCard({
  docType,
  applicationId,
  existing,
  onChanged,
  disabled,
  forceIsFullAmount = null,
  hideFullAmountToggle = false,
  initialAmount = null,
  lockAmount = false,
}) {
  const cfg = DOC_FIELDS[docType];
  const hasExisting = !!existing?.id;
  const [number, setNumber] = useState(existing?.number || "");
  const [date, setDate] = useState(existing?.date || "");
  const [amount, setAmount] = useState(existing?.amount ?? "");
  const [assetCost, setAssetCost] = useState(existing?.asset_cost ?? "");
  const [files, setFiles] = useState([]);
  const [isFullAmount, setIsFullAmount] = useState(!!existing?.is_full_amount);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setNumber(existing?.number || "");
    setDate(existing?.date || "");
    setAmount(existing?.amount ?? "");
    setAssetCost(existing?.asset_cost ?? "");
    setIsFullAmount(
      forceIsFullAmount === null ? !!existing?.is_full_amount : !!forceIsFullAmount
    );
    setFiles([]);
  }, [existing?.id, forceIsFullAmount]);

  useEffect(() => {
    if (!hasExisting && initialAmount !== null && initialAmount !== undefined) {
      setAmount(initialAmount);
    }
  }, [hasExisting, initialAmount]);

  const handleFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(cfg.multi ? list : list.slice(0, 1));
  };

  const handleSubmit = async () => {
    if (!hasExisting && files.length === 0) {
      toast.error("Please attach at least one file");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      if (hasExisting) fd.append("id", existing.id);
      if (number) fd.append("number", number);
      if (date) fd.append("date", date);
      if (amount !== "" && amount !== null && amount !== undefined)
        fd.append("amount", lockAmount && initialAmount !== null ? initialAmount : amount);
      if (cfg.hasAssetCost && assetCost !== "")
        fd.append("asset_cost", assetCost);
      if (cfg.fullAmountToggle)
        fd.append(
          "is_full_amount",
          (forceIsFullAmount === null ? isFullAmount : forceIsFullAmount) ? "1" : "0"
        );
      files.forEach((f) => fd.append("files[]", f));

      const res = await api.post(
        `/api/mobile/applications/${applicationId}/disbursement-request/documents`,
        fd
      );
      if (res.data?.success) {
        toast.success(res.data.message || "Document saved");
        onChanged?.();
      } else {
        toast.error(res.data?.message || "Failed to save document");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!hasExisting) return;
    if (!window.confirm(`Delete this ${cfg.label}?`)) return;
    setDeleting(true);
    try {
      const res = await api.delete(
        `/api/mobile/applications/${applicationId}/disbursement-request/documents/${existing.id}`
      );
      if (res.data?.success) {
        toast.success("Document deleted");
        onChanged?.();
      } else {
        toast.error(res.data?.message || "Failed to delete");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const fileCount = existing?.file_ids?.length || existing?.files?.length || 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 mb-3">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-sm font-bold text-gray-900 dark:text-white">
            {cfg.label}
          </h4>
          {hasExisting && (
            <p className="text-[10px] text-gray-500 mt-0.5">
              ID: {existing.id} {fileCount > 0 && `• ${fileCount} file(s)`}
            </p>
          )}
        </div>
        {hasExisting && !disabled && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg disabled:opacity-50"
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        {cfg.numberLabel && (
          <div>
            <label className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
              {cfg.numberLabel}
            </label>
            <input
              type="text"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />
          </div>
        )}
        {cfg.dateLabel && (
          <div>
            <label className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
              {cfg.dateLabel}
            </label>
            <input
              type="date"
              value={date || ""}
              onChange={(e) => setDate(e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />
          </div>
        )}
        {cfg.amountLabel && (
          <div>
            <label className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
              {cfg.amountLabel}
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={disabled || lockAmount}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />
          </div>
        )}
        {cfg.hasAssetCost && (
          <div>
            <label className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
              Asset Cost
            </label>
            <input
              type="number"
              value={assetCost}
              onChange={(e) => setAssetCost(e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />
          </div>
        )}
      </div>

      {cfg.fullAmountToggle && !hideFullAmountToggle && (
        <div className="mb-3 text-xs text-gray-700 dark:text-gray-300">
          <p className="mb-1">
            Does this {cfg.label.toLowerCase()} cover the full amount?
          </p>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name={`fullAmount-${docType}-${existing?.id || "new"}`}
                checked={isFullAmount === true}
                onChange={() => setIsFullAmount(true)}
                disabled={disabled}
                className="accent-indigo-600"
              />
              Yes
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name={`fullAmount-${docType}-${existing?.id || "new"}`}
                checked={isFullAmount === false}
                onChange={() => setIsFullAmount(false)}
                disabled={disabled}
                className="accent-indigo-600"
              />
              No
            </label>
          </div>
        </div>
      )}

      {!disabled && (
        <div className="flex flex-col sm:flex-row gap-2">
          <label className="flex-1 cursor-pointer">
            <input
              type="file"
              multiple={cfg.multi}
              onChange={handleFileChange}
              className="hidden"
            />
            <span className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl text-xs font-semibold text-gray-700 dark:text-gray-200 transition-colors">
              <Paperclip className="w-3.5 h-3.5" />
              {files.length > 0
                ? `${files.length} file(s) selected`
                : `Choose file${cfg.multi ? "(s)" : ""}`}
            </span>
          </label>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-xs font-bold hover:shadow-lg active:scale-95 transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {submitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5" />
            )}
            {hasExisting ? "Upload" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ================================================================
 * Section: Document Upload
 * ================================================================ */
function DocumentUploadSection({ data, product, applicationId, onChanged, disabled }) {
  if (!data) return null;
  const totals = data.totals || {};
  const flags = data.flags || {};
  const deferral = data.deferral_status || {};
  const [invoiceFullAmount, setInvoiceFullAmount] = useState(
    !!flags.is_invoice_full_amount
  );
  const [mmrFullAmount, setMmrFullAmount] = useState(
    !!flags.is_mmr_full_amount
  );

  useEffect(() => {
    setInvoiceFullAmount(!!flags.is_invoice_full_amount);
  }, [flags.is_invoice_full_amount]);

  useEffect(() => {
    setMmrFullAmount(!!flags.is_mmr_full_amount);
  }, [flags.is_mmr_full_amount]);

  // Build list of doc-type panels relevant for this product
  const buildSinglePanels = () => {
    const panels = [];
    if (product === "RTS") {
      // DISCOM and DPR (single record each)
      panels.push({
        type: "DISCOM",
        existing: data.discom_upload?.id
          ? {
              id: data.discom_upload.id,
              number: data.discom_upload.discom_application_no,
              date: data.discom_upload.discom_sanction_date,
              amount: data.discom_upload.discom_sanctioned_load,
              files: data.discom_upload.discom_file_upload,
            }
          : null,
      });
      panels.push({
        type: "DPR",
        existing: data.dpr_upload?.id
          ? {
              id: data.dpr_upload.id,
              number: data.dpr_upload.dpr_no,
              date: data.dpr_upload.dpr_date,
              amount: data.dpr_upload.dpr_capacity,
              asset_cost: data.dpr_upload.dpr_asset_cost,
              files: data.dpr_upload.dpr_file_upload,
            }
          : null,
      });
    }
    if (product === "3W") {
      panels.push({
        type: "RTO_FORM",
        existing: data.rto_upload?.id
          ? {
              id: data.rto_upload.id,
              number: data.rto_upload.rto_no,
              date: data.rto_upload.rto_date,
              files: data.rto_upload.rto_file_upload,
            }
          : null,
      });
      panels.push({
        type: "VEHICLE_INSURANCE",
        existing: data.insurance_upload?.id
          ? {
              id: data.insurance_upload.id,
              number: data.insurance_upload.insurance_no,
              date: data.insurance_upload.insurance_date,
              amount: data.insurance_upload.insurance_amount,
              files: data.insurance_upload.insurance_file_upload,
            }
          : null,
      });
    }
    return panels;
  };

  const invoiceUploads = (data.invoice_upload || []).map((it) => ({
    id: it.id,
    number: it.invoice_no,
    date: it.invoice_date,
    amount: it.invoice_amount,
    is_full_amount: flags.is_invoice_full_amount,
    files: it.invoice_file_upload,
  }));
  const mmrUploads = (data.mmr_upload || []).map((it) => ({
    id: it.id,
    number: it.mmr_no,
    date: it.mmr_date,
    amount: it.mmr_amount,
    is_full_amount: flags.is_mmr_full_amount,
    files: it.mmr_file_upload,
  }));

  return (
    <Section title="Document Upload" icon={<Upload className="w-4 h-4" />}>
      {/* Totals summary */}
      {/* <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        <Stat label="Asset Cost" value={formatCurrency(totals.asset_cost)} />
        <Stat label="Margin Money" value={formatCurrency(totals.margin_money)} />
        <Stat label="Margin Deviation" value={formatCurrency(totals.margin_money_deviation)} />
        {product !== "RTS" && (
          <Stat label="Invoice Total" value={formatCurrency(totals.total_invoice_amount)} />
        )}
        <Stat label="MMR Total" value={formatCurrency(totals.total_mmr_amount)} />
      </div> */}

      {/* Invoice uploads (multi) - not for RTS */}
      {product !== "RTS" && (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 mb-3">
            <p className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
              Is Invoice for Full Amount?
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-700 dark:text-gray-300">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="invoice-full-amount-global"
                  checked={invoiceFullAmount === true}
                  onChange={() => setInvoiceFullAmount(true)}
                  disabled={disabled}
                  className="accent-indigo-600"
                />
                Yes
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="invoice-full-amount-global"
                  checked={invoiceFullAmount === false}
                  onChange={() => setInvoiceFullAmount(false)}
                  disabled={disabled}
                  className="accent-indigo-600"
                />
                No
              </label>
            </div>
            {invoiceFullAmount && (
              <p className="mt-2 text-[11px] text-indigo-700 dark:text-indigo-300">
                Invoice amount is fixed to full asset cost ({formatCurrency(totals.asset_cost)}).
              </p>
            )}
          </div>

          <DocList
            title="Invoices"
            items={invoiceUploads}
            docType="INVOICE"
            applicationId={applicationId}
            onChanged={onChanged}
            disabled={disabled}
            deferred={deferral.invoice_deferred}
            fullAmountScope="global"
            globalFullAmount={invoiceFullAmount}
            persistedFullAmount={!!flags.is_invoice_full_amount}
            onGlobalFullAmountChange={setInvoiceFullAmount}
            defaultAmountForNew={totals.asset_cost ?? ""}
          />
        </>
      )}

      {/* MMR uploads (multi) */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 mb-3">
        <p className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
          Is MMR for Full Amount?
        </p>
        <div className="flex items-center gap-4 text-sm text-gray-700 dark:text-gray-300">
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="mmr-full-amount-global"
              checked={mmrFullAmount === true}
              onChange={() => setMmrFullAmount(true)}
              disabled={disabled}
              className="accent-indigo-600"
            />
            Yes
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="mmr-full-amount-global"
              checked={mmrFullAmount === false}
              onChange={() => setMmrFullAmount(false)}
              disabled={disabled}
              className="accent-indigo-600"
            />
            No
          </label>
        </div>
        {mmrFullAmount && (
          <p className="mt-2 text-[11px] text-indigo-700 dark:text-indigo-300">
            MMR amount is fixed to margin money ({formatCurrency(totals.margin_money)}).
          </p>
        )}
      </div>

      <DocList
        title="MMR"
        items={mmrUploads}
        docType="MMR"
        applicationId={applicationId}
        onChanged={onChanged}
        disabled={disabled}
        deferred={deferral.mmr_deferred}
        fullAmountScope="global"
        globalFullAmount={mmrFullAmount}
        persistedFullAmount={!!flags.is_mmr_full_amount}
        onGlobalFullAmountChange={setMmrFullAmount}
        defaultAmountForNew={totals.margin_money ?? ""}
      />

      {/* Single-record panels */}
      {buildSinglePanels().map(({ type, existing }) => (
        <DocumentUploadCard
          key={type}
          docType={type}
          applicationId={applicationId}
          existing={existing}
          onChanged={onChanged}
          disabled={disabled}
        />
      ))}
    </Section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl p-2">
      <p className="text-[10px] font-semibold text-indigo-700 dark:text-indigo-300">
        {label}
      </p>
      <p className="text-xs font-bold text-gray-900 dark:text-white mt-0.5">
        {value}
      </p>
    </div>
  );
}

function DocList({
  title,
  items,
  docType,
  applicationId,
  onChanged,
  disabled,
  deferred,
  fullAmountScope = "per-item",
  globalFullAmount = false,
  persistedFullAmount = false,
  onGlobalFullAmountChange,
  defaultAmountForNew = "",
}) {
  const count = items?.length || 0;
  const showReplacementDraftCard =
    fullAmountScope === "global" && globalFullAmount && !persistedFullAmount;
  const visibleItems = showReplacementDraftCard ? [] : items;
  const visibleCount = visibleItems?.length || 0;
  const hasFullAmount =
    fullAmountScope === "global"
      ? !!globalFullAmount
      : (items || []).some((it) => !!it.is_full_amount);
  const addDisabled = disabled || hasFullAmount;

  // Auto-show a blank entry form when nothing has been uploaded yet, so the
  // user can fill the required document in-place (no extra "Add" click).
  const shouldAutoShow =
    visibleCount === 0 &&
    !disabled &&
    (!hasFullAmount || (fullAmountScope === "global" && globalFullAmount));
  const [showNew, setShowNew] = useState(shouldAutoShow);

  // Keep the new-form visible while there are no items and not blocked.
  useEffect(() => {
    if (shouldAutoShow) setShowNew(true);
  }, [shouldAutoShow]);

  // When user toggles full amount to YES from a previously non-full state,
  // hide old rows and show a single replacement card immediately.
  useEffect(() => {
    if (showReplacementDraftCard && !disabled) {
      setShowNew(true);
    }
  }, [showReplacementDraftCard, disabled]);

  // If a full-amount doc exists, ensure the "new" form is hidden.
  useEffect(() => {
    if (
      hasFullAmount &&
      showNew &&
      !(fullAmountScope === "global" && globalFullAmount && visibleCount === 0)
    ) {
      setShowNew(false);
    }
  }, [hasFullAmount, showNew, fullAmountScope, globalFullAmount, visibleCount]);

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
          {title}
          <span className="ml-2 text-[10px] text-gray-500 normal-case">
            ({visibleCount})
          </span>
          {deferred && (
            <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px]">
              Deferred
            </span>
          )}
        </h4>
        {!disabled && (
          <button
            onClick={() => setShowNew((v) => !v)}
            disabled={addDisabled}
            title={
              hasFullAmount
                ? `A ${title.toLowerCase()} covering the full amount has been uploaded`
                : undefined
            }
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            Add {title}
          </button>
        )}
      </div>

      {hasFullAmount && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mb-2">
          A {title.toLowerCase()} covering the full amount has been selected. Adding more is disabled.
        </p>
      )}

      {showReplacementDraftCard && count > 0 && (
        <p className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-2 py-1 mb-2">
          Previous {title.toLowerCase()} entries are hidden. Saving this full-amount entry will replace them.
        </p>
      )}

      {visibleCount === 0 && !showNew && (
        <p className="text-[11px] text-gray-500 mb-2">No {title.toLowerCase()} uploaded.</p>
      )}

      {visibleItems?.map((item, idx) => (
        <div key={item.id}>
          <div className="text-[10px] font-semibold text-gray-500 mb-1 ml-1">
            {title} #{idx + 1}
          </div>
          <DocumentUploadCard
            docType={docType}
            applicationId={applicationId}
            existing={item}
            onChanged={onChanged}
            disabled={disabled}
            forceIsFullAmount={fullAmountScope === "global" ? globalFullAmount : null}
            hideFullAmountToggle={fullAmountScope === "global"}
            initialAmount={
              fullAmountScope === "global" && globalFullAmount
                ? defaultAmountForNew
                : null
            }
            lockAmount={fullAmountScope === "global" && globalFullAmount}
          />
        </div>
      ))}

      {showNew && (
        <>
          <div className="text-[10px] font-semibold text-indigo-600 mb-1 ml-1">
            New {title}
          </div>
          <DocumentUploadCard
            docType={docType}
            applicationId={applicationId}
            existing={null}
            onChanged={() => {
              if (fullAmountScope === "global" && onGlobalFullAmountChange) {
                onGlobalFullAmountChange(globalFullAmount);
              }
              setShowNew(false);
              onChanged?.();
            }}
            disabled={disabled}
            forceIsFullAmount={fullAmountScope === "global" ? globalFullAmount : null}
            hideFullAmountToggle={fullAmountScope === "global"}
            initialAmount={
              fullAmountScope === "global" && globalFullAmount
                ? defaultAmountForNew
                : null
            }
            lockAmount={fullAmountScope === "global" && globalFullAmount}
          />
        </>
      )}
    </div>
  );
}

/* ================================================================
 * Section: Disbursement Requirement (incl. tranches + initiate)
 * ================================================================ */
function DisbursementRequirementSection({
  data,
  applicationId,
  onChanged,
  disabled,
}) {
  const initial = data || {};
  const [disbursementType, setDisbursementType] = useState(
    initial.disbursement_type || "DISBURSEMENT_FOR_ENTIRE_AMOUNT"
  );
  const [numTranches, setNumTranches] = useState(initial.num_tranches || "");
  const [tranches, setTranches] = useState(initial.tranches || []);
  const [disbursementTo, setDisbursementTo] = useState(
    initial.disbursement_to || "dealer"
  );
  const [beneficiaryId, setBeneficiaryId] = useState(
    initial.selected_beneficiary?.id || ""
  );
  const [submitting, setSubmitting] = useState(false);

  const totalAmount = Number(initial.total_disbursement_amount || 0);
  const bankAccounts = initial.available_bank_accounts || {};
  const beneficiary = initial.selected_beneficiary;

  // Re-build tranches when num changes
  useEffect(() => {
    if (disbursementType !== "TRANCHE_DISBURSEMENT") return;
    const n = Number(numTranches);
    if (!n || n < 2) {
      setTranches([]);
      return;
    }
    setTranches((prev) => {
      const next = [];
      const equalPct = Math.floor((100 / n) * 100) / 100;
      for (let i = 0; i < n; i++) {
        const existing = prev[i];
        next.push({
          tranche_number: i + 1,
          tranche_percentage: existing?.tranche_percentage ?? equalPct,
          tranche_amount:
            existing?.tranche_amount ??
            Math.round((totalAmount * equalPct) / 100),
          is_selected: existing?.is_selected ?? i === 0,
        });
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numTranches, disbursementType]);

  const updateTranche = (idx, patch) => {
    setTranches((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, ...patch } : t))
    );
  };

  const handlePctChange = (idx, val) => {
    const pct = Number(val) || 0;
    updateTranche(idx, {
      tranche_percentage: pct,
      tranche_amount: Math.round((totalAmount * pct) / 100),
    });
  };

  const handleAmtChange = (idx, val) => {
    const amt = Number(val) || 0;
    const pct = totalAmount > 0 ? Math.round((amt / totalAmount) * 10000) / 100 : 0;
    updateTranche(idx, {
      tranche_amount: amt,
      tranche_percentage: pct,
    });
  };

  const selectTranche = (idx) => {
    setTranches((prev) =>
      prev.map((t, i) => ({ ...t, is_selected: i === idx }))
    );
  };

  const totalPct = useMemo(
    () => tranches.reduce((s, t) => s + Number(t.tranche_percentage || 0), 0),
    [tranches]
  );

  const handleInitiate = async () => {
    if (!beneficiaryId) {
      toast.error("Please select a beneficiary bank account");
      return;
    }
    if (disbursementType === "TRANCHE_DISBURSEMENT") {
      if (!numTranches || numTranches < 2) {
        toast.error("Please enter number of tranches (2-5)");
        return;
      }
      if (Math.round(totalPct) !== 100) {
        toast.error(`Tranche percentages must total 100% (currently ${totalPct}%)`);
        return;
      }
      if (!tranches.some((t) => t.is_selected)) {
        toast.error("Select one tranche to disburse");
        return;
      }
    }

    setSubmitting(true);
    try {
      const body = {
        disbursement_type: disbursementType,
        disbursement_to: disbursementTo,
        beneficiary_id: Number(beneficiaryId),
      };
      if (disbursementType === "TRANCHE_DISBURSEMENT") {
        body.num_tranches = Number(numTranches);
        body.tranches = tranches;
      }

      const res = await api.post(
        `/api/mobile/applications/${applicationId}/disbursement-request/initiate`,
        body
      );
      if (res.data?.success) {
        toast.success(res.data.message || "Disbursement initiated");
        onChanged?.();
      } else {
        toast.error(res.data?.message || "Failed to initiate disbursement");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to initiate");
    } finally {
      setSubmitting(false);
    }
  };

  const disbursementToOptions = initial.disbursement_to_options || {
    dealer: "Dealer",
  };

  return (
    <Section title="Disbursement Requirement" icon={<Banknote className="w-4 h-4" />}>
      {/* Type radio */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 mb-3">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
            <input
              type="radio"
              name="disbType"
              value="DISBURSEMENT_FOR_ENTIRE_AMOUNT"
              checked={disbursementType === "DISBURSEMENT_FOR_ENTIRE_AMOUNT"}
              onChange={(e) => setDisbursementType(e.target.value)}
              disabled={disabled}
              className="accent-indigo-600"
            />
            Initiate Disbursement for entire amount
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
            <input
              type="radio"
              name="disbType"
              value="TRANCHE_DISBURSEMENT"
              checked={disbursementType === "TRANCHE_DISBURSEMENT"}
              onChange={(e) => setDisbursementType(e.target.value)}
              disabled={disabled}
              className="accent-indigo-600"
            />
            Initiate Tranche Disbursement
          </label>
        </div>

        <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
          Total Disbursement Amount:{" "}
          <span className="font-bold text-gray-900 dark:text-white">
            {formatCurrency(totalAmount)}
          </span>
        </div>

        {disbursementType === "TRANCHE_DISBURSEMENT" && (
          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-xl">
            <label className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
              Number of Tranches (2-5)
            </label>
            <input
              type="number"
              min="2"
              max="5"
              value={numTranches}
              onChange={(e) => setNumTranches(e.target.value)}
              disabled={disabled}
              className="w-32 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />

            {tranches.length > 0 && (
              <div className="mt-3 space-y-2">
                {tranches.map((t, idx) => (
                  <div
                    key={idx}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-gray-800 dark:text-gray-100">
                        Tranche {t.tranche_number}
                      </span>
                      <label className="flex items-center gap-1 text-[11px] text-gray-700 dark:text-gray-300">
                        <input
                          type="radio"
                          name="trancheSelect"
                          checked={t.is_selected}
                          onChange={() => selectTranche(idx)}
                          disabled={disabled}
                          className="accent-indigo-600"
                        />
                        Selected
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-gray-500 dark:text-gray-400">%</label>
                        <input
                          type="number"
                          value={t.tranche_percentage}
                          onChange={(e) =>
                            handlePctChange(idx, e.target.value)
                          }
                          disabled={disabled}
                          className="w-full px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 dark:text-gray-400">Amount</label>
                        <input
                          type="number"
                          value={t.tranche_amount}
                          onChange={(e) =>
                            handleAmtChange(idx, e.target.value)
                          }
                          disabled={disabled}
                          className="w-full px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <p
                  className={`text-[11px] font-semibold ${
                    Math.round(totalPct) === 100
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  Total: {totalPct.toFixed(2)}% (must equal 100%)
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Disbursement to + beneficiary */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 mb-3 space-y-3">
        <div>
          <label className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
            Disbursement To
          </label>
          <select
            value={disbursementTo}
            onChange={(e) => setDisbursementTo(e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {Object.entries(disbursementToOptions).map(([k, v]) => (
              <option key={k} value={k} className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                {v}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
            {disbursementTo === "dealer" ? "Dealer" : "Recipient"} Name
          </label>
          <input
            type="text"
            value={initial.disbursement_to_name || ""}
            disabled
            className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-700 dark:text-gray-300"
          />
        </div>

        <div>
          <label className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
            Select Beneficiary Bank Account
          </label>
          <select
            value={beneficiaryId}
            onChange={(e) => setBeneficiaryId(e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            <option value="" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
              {Object.keys(bankAccounts).length === 0
                ? "No bank accounts available"
                : "-- Select --"}
            </option>
            {Object.entries(bankAccounts).map(([id, label]) => (
              <option key={id} value={id} className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                {label}
              </option>
            ))}
          </select>
        </div>

        {beneficiary && (
          <div className="p-3 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-indigo-600" />
              <h5 className="text-xs font-bold text-gray-800 dark:text-gray-100">
                Beneficiary Details
              </h5>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <Field label="Account Name" value={beneficiary.account_name} />
              <Field label="Account No." value={beneficiary.account_number} />
              <Field label="Bank" value={beneficiary.bank_name} />
              <Field label="IFSC" value={beneficiary.ifsc_code} />
              <Field label="Type" value={beneficiary.account_type} />
              <Field label="Code" value={beneficiary.beneficiary_code} />
            </div>
          </div>
        )}
      </div>

      <button
        onClick={handleInitiate}
        disabled={disabled || submitting}
        className="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-2xl font-bold hover:shadow-lg active:scale-95 transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        {submitting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Send className="w-4 h-4" />
        )}
        Initiate Disbursement
      </button>

      {initial.disbursement_requested_on && (
        <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
          Requested On:{" "}
          <span className="font-semibold">{initial.disbursement_requested_on}</span>
          {initial.disbursement_requested_by && (
            <>
              {" • By "}
              <span className="font-semibold">
                {initial.disbursement_requested_by}
              </span>
            </>
          )}
        </div>
      )}
    </Section>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-gray-500 dark:text-gray-400">{label}</p>
      <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">
        {value || "-"}
      </p>
    </div>
  );
}

/* ================================================================
 * Page
 * ================================================================ */
export default function DisbursementRequest() {
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(
        `/api/mobile/applications/${applicationId}/disbursement-request`
      );
      if (res.data?.success) {
        setData(res.data.data);
      } else {
        setError(res.data?.message || "Failed to load");
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    load();
  }, [load]);

  const product = data?.product || "";
  const stageLocked = isReadOnlyStage(data?.application_details?.app_current_stage);
  const isCompleted = !!data?.disbursement_requirement?.disbursement_requested_on;
  const disabled = stageLocked;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-white/20 dark:border-gray-800 shadow-lg safe-top">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl active:scale-95"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Disbursement Request
            </h1>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
              App #{applicationId} {product && `• ${product}`}
            </p>
          </div>
          {isCompleted && (
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-[10px] font-bold border border-green-200">
              Requested
            </span>
          )}
          {!isCompleted && stageLocked && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-[10px] font-bold border border-gray-200">
              Read-only
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 px-4 py-4 pb-32">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Failed to load</p>
              <p>{error}</p>
              <button
                onClick={load}
                className="mt-2 px-3 py-1 bg-red-600 text-white rounded-lg text-xs font-semibold"
              >
                Retry
              </button>
            </div>
          </div>
        ) : data ? (
          <>
            <OfferPaymentSummarySection
              data={data.offer_payment_summary}
              product={product}
            />
            <DisbursementChecksSection checks={data.disbursement_checks} />
            <DocumentUploadSection
              data={data.document_upload}
              product={product}
              applicationId={applicationId}
              onChanged={load}
              disabled={disabled}
            />
            <DisbursementRequirementSection
              data={data.disbursement_requirement}
              applicationId={applicationId}
              onChanged={load}
              disabled={disabled}
            />
          </>
        ) : null}
      </div>

      <HelpButton />
    </div>
  );
}
